import Footer from "components/Footer/Footer.jsx";
import ScrollToTop from "components/ScrollToTop";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import routes from "routes.js";

import AdminNavbar from "../../custom_modules/AdminNavbar";
import OnboardingWrapper from "../../custom_modules/OnboardingNew";
import Sidebar from "../../custom_modules/Sidebar";
import { addPath } from "../../filterReducer";
import { PrivateRoute } from "../../modules/privateRoute";
import { WeekConfigProvider, useWeekConfig } from "../../utils/weekConfig";

const AdminContent = props => {
  const dispatch = useDispatch();
  const { isWeekStartReady } = useWeekConfig();

  const [sidebarMini, setSidebarMini] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [filteredRoutes, setFilteredRoutes] = useState(routes);

  const adminHistory = useHistory();
  const location = useLocation();
  const wrapperRef = useRef(null);

  const { selectedOrganization } = props;

  // Redirect based on plan for non-admin/owner
  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization.role !== "admin" &&
      selectedOrganization.role !== "owner" &&
      adminHistory?.location?.pathname === "/user/dashboard"
    ) {
      const isStandardPlan =
        selectedOrganization.plan &&
        (selectedOrganization.plan.name === "Standard" ||
          selectedOrganization.plan.nick_name === "standard-free");

      if (isStandardPlan) {
        adminHistory.push("/user/shift-and-schedule");
      } else {
        // Use "jobs" for operation type (default), "tasks" for service type
        const orgType = selectedOrganization.type || "operation";
        const tasksPath =
          orgType === "operation" ? "/user/jobs" : "/user/tasks";
        adminHistory.push(tasksPath);
      }
    }
  }, [selectedOrganization, adminHistory?.location?.pathname]);

  // Initialize Crisp chat for owners
  useEffect(() => {
    if (localStorage.getItem("selected_organization_role") === "owner") {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = "99c3a618-53c4-44b5-8e95-dc28db5a70a7";
      (function () {
        var d = document;
        var s = d.createElement("script");
        s.src = "https://client.crisp.chat/l.js";
        s.async = 1;
        d.getElementsByTagName("head")[0].appendChild(s);
      })();
      if (localStorage.getItem("user_fullName")) {
        window.$crisp.push([
          "set",
          "user:nickname",
          localStorage.getItem("user_fullName"),
        ]);
      }
      if (localStorage.getItem("email")) {
        window.$crisp.push([
          "set",
          "user:email",
          localStorage.getItem("email"),
        ]);
      }
    }
  }, []);

  // Save organization config to localStorage
  useEffect(() => {
    if (selectedOrganization?.plan?.id) {
      localStorage.setItem(
        "selected_organization_plan",
        selectedOrganization.plan.name,
      );
    }
    if (selectedOrganization?.configuration?.id) {
      localStorage.setItem(
        "date_format",
        selectedOrganization.configuration.date_format,
      );
      localStorage.setItem(
        "start_day_of_week",
        selectedOrganization.configuration.start_day_of_week,
      );
    }
  }, [selectedOrganization]);

  // Scroll to top on route change
  useEffect(() => {
    return adminHistory.listen((location, action) => {
      if (action === "PUSH" && wrapperRef?.current) {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
        wrapperRef.current.scrollTop = 0;
      }
    });
  }, [adminHistory]);

  // Filter routes for projects and jobs based on organization type
  useEffect(() => {
    if (!selectedOrganization || !selectedOrganization.id) {
      setFilteredRoutes(routes);
      return;
    }

    const orgType = selectedOrganization.type || "operation";

    const filterRoutes = routesArr => {
      return routesArr
        .map(route => {
          if (route.collapse && route.views) {
            return { ...route, views: filterRoutes(route.views) };
          }
          return route;
        })
        .filter(route => {
          if (route.collapse && route.views && route.views.length === 0)
            return false;
          // If route has no type, include it (common routes)
          if (!route.type) return true;
          // Check if route type matches organization type
          return route.type === orgType;
        });
    };

    const allowedRoutes = filterRoutes(routes);
    setFilteredRoutes(allowedRoutes);
  }, [selectedOrganization?.id, selectedOrganization?.type]);

  const getRoutes = routesArray => {
    const routes = [];
    routesArray.forEach((prop, key) => {
      if (prop.collapse && prop.views) {
        // Flatten nested routes from collapse views
        const nestedRoutes = getRoutes(prop.views);
        routes.push(...nestedRoutes);
      } else if (prop.layout === "/user") {
        routes.push(
          <PrivateRoute
            key={prop.path || key}
            path={prop.layout + prop.path}
            restricted={prop.restricted}
            selectedOrganization={selectedOrganization}
            component={prop.component}
          />,
        );
      }
    });
    return routes;
  };

  const handleMiniClick = () => {
    setSidebarMini(!sidebarMini);
    document.body.classList.toggle("sidebar-mini");
  };

  const handleNavOpen = () => setNavOpen(!navOpen);
  const clearNavOpen = () => setNavOpen(false);

  useEffect(() => {
    dispatch(addPath(location.pathname));
  }, [location.pathname, dispatch]);

  if (!isWeekStartReady) {
    return <div style={{ padding: "2rem", textAlign: "center" }} />;
  }

  return (
    <OnboardingWrapper
      selectedOrganization={selectedOrganization}
      userRole={props.userRole}
      onInviteMember={props.onInviteMember}
      isInviteLoading={props.isInviteLoading}
      memberInvited={props.memberInvited}
      clearMemberInvited={props.clearMemberInvited}
      updateFirstLogin={props.updateFirstLogin}
      loginProfileCreatedReset={props.loginProfileCreatedReset}
    >
      {!isWeekStartReady ? (
        <div style={{ padding: "2rem", textAlign: "center" }}></div>
      ) : (
        <div className="wrapper" ref={wrapperRef}>
          <Sidebar
            {...props}
            routes={filteredRoutes}
            handleMiniClick={handleMiniClick}
            sidebarMini={sidebarMini}
            navOpen={navOpen}
            clearNavOpen={clearNavOpen}
            handleNavOpen={handleNavOpen}
          />
          <div className="main-panel">
            <AdminNavbar
              sidebarState={sidebarMini}
              {...props}
              handleMiniClick={handleMiniClick}
              handleNavOpen={handleNavOpen}
            />
            <Switch>
              {getRoutes(filteredRoutes)}
              <Route path="/user/*">
                <Redirect to="/user/dashboard" />
              </Route>
            </Switch>
            <ScrollToTop />
          </div>
        </div>
      )}
    </OnboardingWrapper>
  );
};

const Admin = props => {
  return (
    <WeekConfigProvider selectedOrganization={props.selectedOrganization}>
      <AdminContent {...props} />
    </WeekConfigProvider>
  );
};

export default Admin;
